/**
 * Schema-driven classifier and masker for sensitive HITL fields.
 *
 * Mirror of the Python module at ``src/workflows/sensitivity.py``.
 * Parity is verified by the shared fixture at
 * ``tests/fixtures/sensitivity_cases.json`` which both the Python and
 * TypeScript test suites consume.
 *
 * Sensitivity kinds
 * -----------------
 *
 * ``"password"`` — interactive password.  Masked with ``"*".repeat(len)``
 *   so the viewer can tell "I typed 12 characters" without seeing the value.
 *
 * ``"secret"`` — non-password secret (MFA OTP, API token).  Masked with
 *   the flat ``[REDACTED]`` placeholder — length never revealed.
 *
 * ``"pii"`` — personally identifiable information (SSN, account number).
 *   Masked with ``[REDACTED]`` — same as ``"secret"``.
 *
 * Detection priority
 * ------------------
 *
 * 1. ``schemaProperty["x-sensitive"]`` — explicit annotation.
 * 2. ``schemaProperty["format"] === "password"`` — back-compat synonym.
 * 3. ``key`` in the password-fallback set — case-insensitive defense.
 */

export type SensitiveKind = "password" | "secret" | "pii";

export const VALID_KINDS: ReadonlySet<string> = new Set(["password", "secret", "pii"]);

/** Flat placeholder for secret/pii kinds. Never reveals length. */
export const REDACTED_PLACEHOLDER = "[REDACTED]";

/** Key names that always imply password-class sensitivity (case-insensitive). */
const PASSWORD_FALLBACK_KEYS = new Set(["password", "passwd", "pwd"]);

/**
 * Return the sensitivity kind for a field, or ``null`` if not sensitive.
 *
 * @param schemaProperty - The JSON Schema property object for this field,
 *   or ``undefined`` / ``null`` when no schema is available.
 * @param key - The field key name (used for the key-name fallback).
 */
export function classifySensitivity(
  schemaProperty: Record<string, unknown> | undefined | null,
  key?: string,
): SensitiveKind | null {
  if (schemaProperty != null) {
    const xs = schemaProperty["x-sensitive"];
    if (xs !== undefined) {
      if (xs === true) return "secret";
      if (typeof xs === "string") {
        if (VALID_KINDS.has(xs)) return xs as SensitiveKind;
        // Invalid value — log a warning rather than throwing so the UI
        // degrades gracefully even if a workflow author made a typo.
        console.warn(`[sensitivity] invalid x-sensitive value: ${String(xs)}`);
        return null;
      }
    }
    if (schemaProperty["format"] === "password") return "password";
  }

  if (key !== undefined && PASSWORD_FALLBACK_KEYS.has(key.toLowerCase())) {
    return "password";
  }

  return null;
}

/**
 * Return the appropriate mask string for a cleartext value.
 *
 * - ``"password"``: ``"*".repeat(String(value).length)`` — preserves length.
 * - ``"secret"`` / ``"pii"``: ``REDACTED_PLACEHOLDER`` — never reveals length.
 */
export function maskValue(value: unknown, kind: SensitiveKind): string {
  if (kind === "password") {
    const s = value == null ? "" : String(value);
    return "*".repeat(s.length);
  }
  return REDACTED_PLACEHOLDER;
}

/**
 * Recursively redact sensitive values in an arbitrary JSON tree using the
 * corresponding JSON Schema.
 *
 * Used to sanitise the depth-3 raw-JSON ``<details>`` dump in
 * ``HitlResponseRenderer`` before calling ``JSON.stringify``.
 */
export function redactTree(
  value: unknown,
  schema: Record<string, unknown> | undefined | null,
): unknown {
  if (value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    const itemsSchema =
      schema != null && typeof schema["items"] === "object"
        ? (schema["items"] as Record<string, unknown>)
        : null;
    return value.map((item) => redactTree(item, itemsSchema));
  }

  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const props =
      schema != null && typeof schema["properties"] === "object"
        ? (schema["properties"] as Record<string, Record<string, unknown>>)
        : {};
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      const subSchema = props[k] ?? null;
      const kind = classifySensitivity(subSchema, k);
      if (kind !== null) {
        out[k] = maskValue(v, kind);
      } else {
        out[k] = redactTree(v, subSchema);
      }
    }
    return out;
  }

  return value;
}

/**
 * Whether debug-view bypasses (depth-3 raw-JSON ``<details>``, JSON fallback
 * textarea) should skip redaction so values remain visible during local
 * development.
 *
 * Returns ``true`` in non-production builds; ``false`` in production.
 * Uses ``process.env.NODE_ENV`` (compatible with both Jest/CommonJS and Vite/ESM)
 * so the production bundle always redacts, regardless of runtime configuration.
 */
export function debugViewsBypassRedaction(): boolean {
  return process.env.NODE_ENV !== "production";
}
